import { z } from 'zod';
import { createRpcContract, createRpcCaller } from '@/lib/rpc';

// ============================================
// TYPES
// ============================================

export interface AdminUser {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  tier: 'free' | 'power_rack' | 'trial';
  trialEndsAt: Date | null;
  isAdmin: boolean;
  createdAt: Date;
  lastActiveAt: Date | null;
  workoutCount: number;
  programCount: number;
}

export interface AdminUserDetail extends AdminUser {
  subscriptionId: string | null;
  subscriptionPlatform: 'ios' | 'android' | 'web' | null;
  subscriptionExpiresAt: Date | null;
  adminNotes: string | null;
  syncDevices: SyncDevice[];
  recentActivity: ActivityItem[];
}

export interface SyncDevice {
  id: string;
  name: string;
  platform: string;
  lastSyncAt: Date;
}

export interface ActivityItem {
  type: 'workout' | 'program_created' | 'program_submitted' | 'subscription_changed';
  description: string;
  timestamp: Date;
}

// ============================================
// RPC CONTRACTS
// ============================================

// Search users
export const searchUsersContract = createRpcContract(
  'admin_search_users',
  z.object({
    p_query: z.string().optional(),
    p_tier: z.enum(['free', 'power_rack', 'trial']).optional(),
    p_limit: z.number().optional(),
    p_offset: z.number().optional(),
  }),
  z.object({
    users: z.array(
      z.object({
        id: z.string(),
        email: z.string().nullable(),
        display_name: z.string().nullable(),
        avatar_url: z.string().nullable(),
        tier: z.enum(['free', 'power_rack', 'trial']),
        trial_ends_at: z.string().nullable(),
        is_admin: z.boolean(),
        created_at: z.string(),
        last_active_at: z.string().nullable(),
        workout_count: z.number(),
        program_count: z.number(),
      })
    ),
    total: z.number(),
  })
);

// Get user detail
export const getUserDetailContract = createRpcContract(
  'admin_get_user_detail',
  z.object({
    p_user_id: z.string().uuid(),
  }),
  z.object({
    user: z.object({
      id: z.string(),
      email: z.string().nullable(),
      display_name: z.string().nullable(),
      avatar_url: z.string().nullable(),
      tier: z.enum(['free', 'power_rack', 'trial']),
      trial_ends_at: z.string().nullable(),
      is_admin: z.boolean(),
      created_at: z.string(),
      last_active_at: z.string().nullable(),
      workout_count: z.number(),
      program_count: z.number(),
      subscription_id: z.string().nullable(),
      subscription_platform: z.enum(['ios', 'android', 'web']).nullable(),
      subscription_expires_at: z.string().nullable(),
      admin_notes: z.string().nullable(),
    }),
    sync_devices: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        platform: z.string(),
        last_sync_at: z.string(),
      })
    ),
    recent_activity: z.array(
      z.object({
        type: z.enum(['workout', 'program_created', 'program_submitted', 'subscription_changed']),
        description: z.string(),
        timestamp: z.string(),
      })
    ),
  })
);

// Update user tier
export const updateUserTierContract = createRpcContract(
  'admin_update_user_tier',
  z.object({
    p_user_id: z.string().uuid(),
    p_tier: z.enum(['free', 'power_rack', 'trial']),
    p_trial_days: z.number().optional(), // For extending trial
  }),
  z.object({
    success: z.boolean(),
  })
);

// Update admin notes
export const updateAdminNotesContract = createRpcContract(
  'admin_update_user_notes',
  z.object({
    p_user_id: z.string().uuid(),
    p_notes: z.string(),
  }),
  z.object({
    success: z.boolean(),
  })
);

// Export user data
export const exportUserDataContract = createRpcContract(
  'admin_export_user_data',
  z.object({
    p_target_user_id: z.string().uuid(),
    p_include_audit: z.boolean().optional(),
  }),
  z.object({
    url: z.string().optional(),
    export: z.any().optional(),
  })
);

// Cancel subscription
// NOTE: This RPC is defined in WEB_COMPANION_SYNC_SPEC.md section 8.7.3
// Backend implementation required - will return 'unsupported' for iOS (user must cancel in App Store)
export const cancelSubscriptionContract = createRpcContract(
  'admin_cancel_subscription',
  z.object({
    p_user_id: z.string().uuid(),
    p_cancel_at_period_end: z.boolean().default(true),
  }),
  z.object({
    status: z.enum(['success', 'unsupported', 'error']),
    platform: z.enum(['ios', 'android', 'web']).nullable(),
    message: z.string(),
    effective_end: z.string().nullable(),
  })
);

export type CancelSubscriptionResult = z.infer<typeof cancelSubscriptionContract.outputSchema>;

// ============================================
// RPC CALLERS
// ============================================

export const usersRpc = {
  searchUsers: createRpcCaller(searchUsersContract),
  getUserDetail: createRpcCaller(getUserDetailContract),
  updateUserTier: createRpcCaller(updateUserTierContract),
  updateAdminNotes: createRpcCaller(updateAdminNotesContract),
  exportUserData: createRpcCaller(exportUserDataContract),
  cancelSubscription: createRpcCaller(cancelSubscriptionContract),
};

// ============================================
// TRANSFORM HELPERS
// ============================================

export function transformUser(
  raw: z.infer<typeof searchUsersContract.outputSchema>['users'][number]
): AdminUser {
  return {
    id: raw.id,
    email: raw.email,
    displayName: raw.display_name,
    avatarUrl: raw.avatar_url,
    tier: raw.tier,
    trialEndsAt: raw.trial_ends_at ? new Date(raw.trial_ends_at) : null,
    isAdmin: raw.is_admin,
    createdAt: new Date(raw.created_at),
    lastActiveAt: raw.last_active_at ? new Date(raw.last_active_at) : null,
    workoutCount: raw.workout_count,
    programCount: raw.program_count,
  };
}

export function transformUserDetail(
  raw: z.infer<typeof getUserDetailContract.outputSchema>
): AdminUserDetail {
  return {
    ...transformUser({
      ...raw.user,
      is_admin: raw.user.is_admin,
    }),
    subscriptionId: raw.user.subscription_id,
    subscriptionPlatform: raw.user.subscription_platform,
    subscriptionExpiresAt: raw.user.subscription_expires_at
      ? new Date(raw.user.subscription_expires_at)
      : null,
    adminNotes: raw.user.admin_notes,
    syncDevices: raw.sync_devices.map((d) => ({
      id: d.id,
      name: d.name,
      platform: d.platform,
      lastSyncAt: new Date(d.last_sync_at),
    })),
    recentActivity: raw.recent_activity.map((a) => ({
      type: a.type,
      description: a.description,
      timestamp: new Date(a.timestamp),
    })),
  };
}
